import I18n from 'app/i18n';
import logEvent from 'app/amplitude/log_event';
import {toastError, toastSuccess} from 'app/components/toasts/liteToast';
import {shopStore, userStore} from 'app/store/global';
import {slowConnectionPromise} from 'app/utils';
import Post from 'app/utils/Post';
import type UserInterface from 'app/interfaces/UserInterface';
import type UserPurchaseInterface from 'app/interfaces/shop/UserPurchaseInterface';

type ExchangeUserBalance = Pick<UserInterface, 'balance' | 'demo_balance' | 'play_money' | 'points'>;

interface ExchangeResponse {
    has_more?: boolean;
    message?: string;
    purchases?: UserPurchaseInterface[];
    success?: boolean;
    user?: ExchangeUserBalance;
}

class ShopExchangeController {
    exchange(points: number): Promise<boolean> {
        return slowConnectionPromise(
            new Post({
                params: {points},
                url: '/shop/exchange'
            })
                .execute()
                .then((httpResponse: Response) => httpResponse.json())
                .then((response: ExchangeResponse) => this.handleResponse(points, response))
                .catch(() => {
                    toastError(I18n.t('server_error_try_later'));
                    return false;
                })
        ) as Promise<boolean>;
    }

    private handleResponse(points: number, response: ExchangeResponse): boolean {
        if (response.success) {
            this.applySuccess(points, response);
            return true;
        }

        toastError(response.message || `${I18n.t('server_error_try_later')}`);
        return false;
    }

    private applySuccess(points: number, response: ExchangeResponse): void {
        logEvent(
            'SHOP_ITEM_PURCHASED',
            {
                item: I18n.t('money_header'),
                price: points,
                real_money: false,
                type: 'money'
            }
        );
        toastSuccess(I18n.t('purchase_was_successful_message'));

        if (response.user) {
            this.applyUserBalance(response.user);
        }

        if (response.purchases) {
            shopStore.updatePurchasesData(response.purchases, Boolean(response.has_more), 1);
        }

        shopStore.updateShop(false);
    }

    private applyUserBalance(user: ExchangeUserBalance): void {
        userStore.update({user: user as UserInterface});
        shopStore.setData({
            ...shopStore.data,
            user: {
                money: String(user.balance),
                points: String(user.points)
            }
        });
    }
}

export const shopExchangeController = new ShopExchangeController();
